import { Combobox, ComboboxChip, ComboboxChips, ComboboxChipsInput, ComboboxContent, ComboboxEmpty, ComboboxItem, ComboboxList, ComboboxValue, useComboboxAnchor } from "@/ds/shadcn/components/ui/combobox";
import type { Option } from "@/lib/common/Option";
import { useCallback, useMemo } from "react";

export interface MultiSelectorProps<T extends string> {
	className?: string;
	placeholder: string;
	options: Option<T>[];
	values: T[];
	onChange: (values: T[]) => void;
}

export const MultiSelector = <T extends string>(props: MultiSelectorProps<T>): React.JSX.Element => {
	const {
		placeholder,
		options,
		values,
		onChange
	} = props;

	const anchor = useComboboxAnchor();

	const labelToValueMapping = useMemo(() => {
		const mapping: Record<string, T> = {};
		options.forEach(x => mapping[x.label] = x.value);

		return mapping;
	}, [options]);

	const valueToLabelMapping = useMemo(() => {
		const mapping: Record<T, string> = {} as Record<T, string>;
		options.forEach(x => mapping[x.value] = x.label);

		return mapping;
	}, [options]);

	const labels = useMemo(() => Object.keys(labelToValueMapping), [labelToValueMapping]);
	const selectedLabels = useMemo(() => values.map(value => valueToLabelMapping[value]), [values, valueToLabelMapping]);
	const onValueChange = useCallback((selectedLabels: string[]) => {
		const selectedValues = selectedLabels.map(label => labelToValueMapping[label]);
		onChange(selectedValues);
	}, [labelToValueMapping, onChange]);

	return (
		<Combobox multiple autoHighlight items={labels} value={selectedLabels} onValueChange={onValueChange} modal={true}>
			<ComboboxChips ref={anchor} className="w-full">
				<ComboboxValue>
					{(values) => (
						<>
							{
								values.map((value: string) => (
									<ComboboxChip key={value}>{value}</ComboboxChip>
								))
							}
							<ComboboxChipsInput placeholder={values.length > 0 ? "" : placeholder} />
						</>
					)}
				</ComboboxValue>
			</ComboboxChips>
			<ComboboxContent anchor={anchor}>
				<ComboboxEmpty>No items found.</ComboboxEmpty>
				<ComboboxList>
					{(item) => (
						<ComboboxItem key={item} value={item}>
							{item}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
